import { createContext, useCallback, useContext, useEffect } from 'react'
import { mixtilesAxios } from '../utils/ApiUtils'
import { getAuthRequestConfig } from '../services/LoginUtills'
import { Subject } from 'rxjs'
import { useAuth } from '../services/AuthProvider/AuthProvider'
import { getFreshIdToken } from '../services/AuthProvider/cognito'
import { logger } from '../services/logger'

const ApiContext = createContext()

ApiContext.displayName = 'ApiContext'

/**
 * @param {string} method
 * @param {string} path
 * @param {object | null} [params]
 * @param {boolean} [requiresAuthentication]
 */
export const createApiFunction = (
  method,
  path,
  params = null,
  requiresAuthentication = false
) => ({
  method,
  path,
  params,
  requiresAuthentication,
})

export const API_METHODS = {
  POST: 'post',
  GET: 'get',
  PUT: 'put',
}

export default function useApi() {
  const context = useContext(ApiContext)

  if (context === undefined) {
    throw new Error('useApi must be used within a apiContextProvider')
  }

  return context
}

export function withApi(Component) {
  return function Comp(props) {
    return (
      <ApiContext.Consumer>
        {(contexts) => <Component {...props} api={contexts} />}
      </ApiContext.Consumer>
    )
  }
}

const apiSubject = { sub: new Subject() }

// We need this so we can trigger api function from everywhere including "managers"
// that are not react components since they can't use the context
export const triggerApi = (apiFunc) => {
  apiSubject.sub.next({ apiFunc })
}

export const callApi = (apiFunc) => {
  return new Promise((resolve, reject) => {
    apiSubject.sub.next({ apiFunc, promise: { resolve, reject } })
  })
}

function isJwtExpiredError(error) {
  const status = error?.response?.status
  const message = error?.response?.data?.message || error?.message || ''
  return status === 401 && message.includes('jwt expired')
}

export function ApiProvider({ children }) {
  // We currently have two authentication providers that login in the user with Auth0.
  // Auth0Provider is a universal login mechanism package provided by Auth0,
  // Whereas AuthProvider is an embedded login mechanism we developed.
  // Therefore, when accessing the api with token, we check whether either provided has authenticated and if
  // so we use the access token provided by it.
  // This needs to be changed in the future as we move to the embedded login flow
  const { isAuthenticated, getIdToken } = useAuth()

  useEffect(() => {
    // This is done to make sure the latest subscribe is with the most updated isAuthenticated flag
    if (apiSubject.sub.observers.length > 0) {
      apiSubject.sub.unsubscribe()
      apiSubject.sub = new Subject()
    }

    apiSubject.sub.subscribe(apiTriggered)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  const apiTriggered = useCallback(
    async ({ apiFunc, promise }) => {
      try {
        const result = await call(apiFunc)
        promise?.resolve(result)
      } catch (e) {
        if (promise) {
          promise?.reject(e)
          return
        }

        // If we don't have a promise, we need to log the error since nobody else can
        logger.error('API triggered error', e)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated]
  )

  const call = useCallback(
    async (
      { method, path, params, headers, requiresAuthentication = false },
      numRetries,
      onFailedAttempt
    ) => {
      if (requiresAuthentication && !isAuthenticated) {
        return
      }

      const getRequestToken = async (fetchToken) => {
        const token = isAuthenticated ? await fetchToken() : null

        // A lost session leaves `isAuthenticated` true until AuthProvider's reload lands;
        // sending now would omit the Authorization header
        if (requiresAuthentication && !token) {
          throw new Error(`Missing auth token for ${path}`)
        }
        return token
      }

      const makeRequest = async (token) => {
        const requestHeaders = { ...headers, ...getAuthRequestConfig(token) }

        const request = {
          [API_METHODS.POST]: post,
          [API_METHODS.GET]: get,
          [API_METHODS.PUT]: put,
        }

        return request[method]({
          path,
          params,
          headers: requestHeaders,
          numRetries,
          onFailedAttempt,
        })
      }

      const token = await getRequestToken(getIdToken)

      try {
        return await makeRequest(token)
      } catch (error) {
        if (isAuthenticated && isJwtExpiredError(error)) {
          logger.info('JWT expired, retrying with fresh token', { path })
          const freshToken = await getRequestToken(getFreshIdToken)
          return makeRequest(freshToken)
        }
        throw error
      }
    },
    [isAuthenticated, getIdToken]
  )

  const post = async ({
    path,
    params,
    headers,
    numRetries,
    onFailedAttempt,
  }) => {
    const response = await mixtilesAxios.post(
      path,
      params,
      headers,
      numRetries,
      onFailedAttempt
    )
    return response.data
  }

  const put = async ({
    path,
    params,
    headers,
    numRetries,
    onFailedAttempt,
  }) => {
    const response = await mixtilesAxios.put(
      path,
      params,
      headers,
      numRetries,
      onFailedAttempt
    )
    return response.data
  }

  const get = async ({
    path,
    params,
    headers,
    numRetries,
    onFailedAttempt,
  }) => {
    const requestConfig = {
      ...headers,
      params,
    }
    const response = await mixtilesAxios.get(
      path,
      requestConfig,
      numRetries,
      onFailedAttempt
    )
    return response.data
  }

  return (
    <ApiContext.Provider
      value={{
        call,
        isAuthenticated,
        getToken: getIdToken,
      }}
    >
      {children}
    </ApiContext.Provider>
  )
}
